import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { EmployeeMinResponse } from 'types/EmployeeTypes';
import { buildQueryString } from 'utils/url';

import { APIResponse, BUSMAN_TAGS, busmanApiSlice } from './busmanApiSlice';
import { getApiErrorMessage } from './errors';
import { GetEmployeeDetailsResponse, GetManagerListResponse, GetTaggingListJobsResponse } from './types';

export const userEndpoints = busmanApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getEmployeeDetails: builder.query({
      query: ({ empID, getAddress = false }) =>
        `/user/${buildQueryString({
          employee_id: empID,
          address: getAddress,
        })}`,
      transformResponse: (response: GetEmployeeDetailsResponse) => response.data,
      providesTags: [BUSMAN_TAGS.User],
    }),

    // Update Employee Details
    updateEmployeeDetail: builder.mutation({
      query: (employeeDetails) => {
        return {
          url: 'user/update/',
          method: 'put',
          body: employeeDetails,
        };
      },
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
      invalidatesTags: [BUSMAN_TAGS.Team, BUSMAN_TAGS.Worklog, BUSMAN_TAGS.User],
    }),

    //Fetch all managers
    getManagerList: builder.query({
      query: () => '/user/managersadmins/',
      transformResponse: (response: GetManagerListResponse) => response.data,
    }),

    //Get Tagging List - Worklogs
    getTaggingListWorklogs: builder.query({
      query: () => '/user/worklog/tags/',
      transformResponse: (response: GetTaggingListJobsResponse) => response.data,
    }),

    //Get Tagging List - Jobs
    getTaggingListJobs: builder.query({
      query: () => '/user/job/tags/',
      transformResponse: (response: GetTaggingListJobsResponse) => response.data,
    }),
  }),
});

export const {
  useGetManagerListQuery,
  useGetTaggingListWorklogsQuery,
  useLazyGetTaggingListJobsQuery,
  useLazyGetTaggingListWorklogsQuery,
  useGetTaggingListJobsQuery,
  useUpdateEmployeeDetailMutation,
  useLazyGetEmployeeDetailsQuery,
} = userEndpoints;

// All Employee Based Endpoints

export const employeeEndpoints = busmanApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllEmployees: builder.query({
      query: () => '/user/all/?minimised=True',
      transformResponse: (response: APIResponse<EmployeeMinResponse[]>) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
      providesTags: [BUSMAN_TAGS.User],
    }),

    createEmployee: builder.mutation({
      query: (data) => {
        return {
          url: '/user/create/',
          method: 'post',
          body: data,
        };
      },
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
      invalidatesTags: [BUSMAN_TAGS.User],
    }),
  }),
});

export const { useGetAllEmployeesQuery, useCreateEmployeeMutation } = employeeEndpoints;
